import { useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { ConferenceRoomGate } from './comms/conference-room'
import { createConferenceJoinLoader } from './comms/conference-room/conferenceRoomLoader'
import type { ConferenceRoomMetadata } from './comms/conference-room'
import { CONFERENCE_CALLS_ROOT } from './comms/conferenceCallsData'

/**
 * V2 Conference Room — in-app custom Daily "black studio" call surface.
 *   /square/command-center/communications/conference-rooms/:meetingId
 *
 * Registered as an AccessGate-protected top-level route (see router.tsx),
 * mounted full-screen like the rest of the V2 command center. The flow:
 *   Conference Calls -> Join -> this route (AccessGate) -> ConferenceRoomGate
 *   -> real Vercel join API (memory-only scoped token) -> explicit camera/mic
 *   consent -> custom preview -> Join -> Square Code tiles/controls -> Leave
 *   -> provider leave + destroy -> back to Conference Calls.
 *
 * Only the non-secret meetingId appears in the URL. The scoped short-lived token
 * is fetched by the gate and held in memory only. Provider credentials
 * (DAILY_API_KEY) never reach the browser. No Daily chrome, no new tab.
 *
 * Display-only metadata (title/mode) is passed via navigation state when the
 * operator clicks Join; on a direct link/refresh it falls back to honest
 * generic labels (the token fetch + authorization are unaffected).
 */
export default function SquareCodeConferenceRoomRoute() {
  const navigate = useNavigate()
  const location = useLocation()
  const { meetingId = '' } = useParams()
  const { user, isAdmin } = useAuth()

  const displayName = useMemo(() => {
    const email = user?.email ?? ''
    const local = email.split('@')[0]
    return local || 'Operator'
  }, [user?.email])

  const loadJoinSession = useMemo(
    () => createConferenceJoinLoader(displayName, user?.id),
    [displayName, user?.id],
  )

  const navState = (location.state ?? {}) as Partial<ConferenceRoomMetadata>
  const metadata: ConferenceRoomMetadata = {
    title: navState.title || 'Conference room',
    meetingMode: navState.meetingMode,
    roomName: navState.roomName || meetingId,
    protectedRoom: navState.protectedRoom,
    channelId: navState.channelId ?? null,
  }

  return (
    <ConferenceRoomGate
      meetingId={meetingId}
      metadata={metadata}
      loadJoinSession={loadJoinSession}
      displayName={displayName}
      // Evaluated fresh from the live auth context on every render — NOT
      // routed through navigation state (metadata), which is a one-time
      // snapshot at Join time and can go stale on refresh/direct link.
      // approve.ts itself requires role level >= 80; isAdmin is exactly
      // that same threshold, already computed by AuthContext.
      canDecideApprovals={isAdmin}
      onExit={() => navigate(CONFERENCE_CALLS_ROOT)}
    />
  )
}
